import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import { StatusPill } from "../../Components/Needed";
import ListLayout from "../../Components/List";
import BrandFormModal from "./BrandFormModal";
import useListParams from "../../Helpers/useListParams";

const INITIAL_FILTERS = { search: "", searchKey: "brand", count: 10, brandType: "", status: "", sortBy: "createdAt", sort: false };

const SEARCH_KEYS = [{ key: "brand", value: "Brand name" }];

const FILTER_ITEMS = {
    dropdown: [
        {
            key: "brandType",
            name: "Type",
            options: [{ key: "", name: "All" }, { key: "cars", name: "Cars" }, { key: "bikes", name: "Bikes" }],
        },
        {
            key: "status",
            name: "Status",
            options: [{ key: "", name: "All" }, { key: true, name: "Active" }, { key: false, name: "Inactive" }],
        },
    ],
    sort: [
        { key: "brand", name: "Name" },
        { key: "createdAt", name: "Created" },
    ],
};

const BrandList = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/admin/vehicle/brand/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.brandType ? { brandType: filters.brandType } : {}),
                ...(filters.status !== "" ? { status: filters.status } : {}),
                sortBy: filters.sortBy,
                sort: filters.sort,
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.brandType, filters.status, filters.sortBy, filters.sort]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const columns = [
        {
            key: "brand",
            name: "Brand",
            mandatory: true,
            render: (row) => (
                <div className="d-flex align-items-center gap-2">
                    {row.logo ? <img src={row.logo} alt="" className="catalog-logo-thumb" /> : <div className="catalog-logo-thumb catalog-logo-placeholder" />}
                    <span>{row.brand}</span>
                </div>
            ),
        },
        { key: "brandType", name: "Type", render: (row) => <span className="text-capitalize">{row.brandType}</span> },
        { key: "status", name: "Status", render: (row) => <StatusPill active={row.status} /> },
        { key: "createdAt", name: "Created", render: (row) => formatDateTime(row.createdAt) },
    ];

    return (
        <>
            <ListLayout
                title="Brands"
                description="Vehicle manufacturers - the top of the Brand -> Model -> Variant catalog."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={FILTER_ITEMS}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by brand name..."
                onRowClick={(row) => navigate(`/brand/${row.brandId}`)}
                addList={() => setShowAddModal(true)}
            />

            {showAddModal ? (
                <BrandFormModal
                    mode="create"
                    onClose={() => setShowAddModal(false)}
                    onSaved={() => { setShowAddModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default BrandList;
